/**
 * **상품 랭킹 질문** — 파라미터를 보내고 도메인 타입을 받는다. 질문 8개가 여기 있다.
 *
 * ```
 *   sections/market/…            취소 · 로딩 · 캐시 (여기 없다)
 *     │ fetchProductRankingBoard(params)
 *   이 파일                       질문 — op 이름과 결과 모양을 안다
 *     │ els-request.ts           전송 (dedupe · 2초 캐시)
 *     ▼ POST /api/els/product-rankings   { op, params }
 *   server/product-rankings/      답한다
 * ```
 *
 * ## ⚠️ 왜 `callOp(op, params)` 하나가 아닌가
 *
 * 그러면 결과 타입이 `ProductRankingOpResponse<K>`가 되고, HTTP를 건너온 `unknown`을
 * 거기 맞추려면 **타입 단언이 필요하다.** 이 리포는 단언을 쓰지 않는다. 그래서 op마다
 * **결과를 구조적으로 확인하는 함수 하나**를 둔다 — `actions/els/moment.ts`와 같은 규약이다.
 *
 * ## ⚠️ 필드를 하나씩 다시 세지 않는다
 *
 * `items`·`facets`·`columns`는 곧장 화면으로 흘러가고 화면이 빈 값을 견디게 돼 있다.
 * 여기서 다시 세면 **서버 타입과 두 벌**이 되고 필드가 늘 때마다 양쪽을 고쳐야 한다.
 * 확인하는 것은 *"에러가 아니고, 닻이 되는 필드가 있는가"* 뿐이다(`moment.ts` 와 동일).
 *
 * ## ⚠️ mockup 갈래는 질문 첫머리 한 줄이다
 *
 * `rankings.search`·`board`·`detail`·`history`·`crossChannel`·`thumbs` 는 `resolveMock*()` 가 값을 주면 그것으로
 * 끝난다(`actions/products/product-mock` — 상품 BFF 와 한 레지스트리다). 등록이 없으면 늘
 * `null` 이라 실화면은 평소 경로 그대로다.
 *
 * ## ⚠️ 화면이 URL을 만들지 않는다
 *
 * 화면은 op 이름만 안다 — 어느 주소인지도, `?board=1&kind=…` 같은 라우트 분기 플래그도
 * 모른다. 화면에서 주소를 조립하면 이 경계가 무너진다.
 */

import type { ProductRankingTagFilter } from 'src/types/product-rankings/product-rankings-scope';
import type {
  ProductRankingFacets,
  ProductRankingBucketMap,
  ProductRankingSearchFacets,
} from 'src/types/product-rankings/product-rankings-stats';
import type {
  ProductRankingSearchItem,
  ProductRankingHistoryPoint,
  ProductRankingHistorySummary,
} from 'src/types/product-rankings/product-rankings-domain';
import type {
  ProductRankingThumb,
  ProductRankingRefCard,
  ProductRankingBoardRequest,
  ProductRankingBoardResponse,
  ProductCatalogGrowthRequest,
  ProductRankingDetailRequest,
  ProductDetailVersionRequest,
  ProductRankingSearchRequest,
  ProductRankingThumbsRequest,
  ProductRankingByRefsRequest,
  ProductCatalogGrowthResponse,
  ProductDetailVersionResponse,
  ProductRankingDetailResponse,
  ProductRankingSearchResponse,
  ProductRankingHistoryRequest,
  ProductRankingThumbsResponse,
  ProductRankingByRefsResponse,
  ProductRankingHistoryResponse,
  ProductRankingCrossChannelRequest,
  ProductRankingCrossChannelResponse,
  ProductRankingAvailableDatesRequest,
  ProductRankingAvailableDatesResponse,
} from 'src/types/product-rankings/product-rankings-ops';

import { createElsProxyRequest } from 'src/actions/els/els-request';
import {
  resolveMockProductRankingBoard,
  resolveMockProductCrossChannel,
  resolveMockProductRankingDetail,
  resolveMockProductRankingSearch,
  resolveMockProductRankingThumbs,
  resolveMockProductRankingHistory,
} from 'src/actions/products/product-mock';

/** 인덱스당 엔드포인트 하나 */
const PRODUCT_RANKINGS_OPS_PATH = '/api/els/product-rankings';

const requestRankingOp = createElsProxyRequest(PRODUCT_RANKINGS_OPS_PATH);

/** 응답이 객체이고 에러가 아닌지 — op마다 반복하던 두 줄 */
function readOpData(data: unknown, who: string): object {
  if (data == null || typeof data !== 'object') throw new Error(`[${who}] 응답이 객체가 아니다`);
  if ('error' in data) throw new Error(`[${who}] ${String(data.error)}`);
  return data;
}

function readNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** 「모른다」를 `0`으로 접으면 안 되는 자리 — 버전의 컷 수·본문 길이 */
function readNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readBool(value: unknown): boolean {
  return value === true;
}

function readList<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * `items` + 페이지네이션 — 목록·검색이 같은 모양이다.
 *
 * ⚠️ 항목 타입을 **인자로 받는다.** 스프레드로 펴면 반환 타입에서 추론이 끊겨
 * `unknown[]`이 되고, 그러면 호출부가 단언을 쓰게 된다.
 */
function readPaged<T>(data: object) {
  return {
    items: readList<T>('items' in data ? data.items : null),
    from: readNumber('from' in data ? data.from : null),
    limit: readNumber('limit' in data ? data.limit : null),
    total: readNumber('total' in data ? data.total : null),
    hasMore: readBool('hasMore' in data ? data.hasMore : null),
    nextFrom: readNumber('nextFrom' in data ? data.nextFrom : null),
  };
}

function readObject(value: unknown): object {
  return value != null && typeof value === 'object' ? value : {};
}

/**
 * 버킷 지도 — `{ 값: 개수 }`.
 *
 * ⚠️ **키는 데이터에서 온다**(브랜드 이름·태그 값). 그래서 키를 세지 않고 값이
 * 숫자인 것만 남긴다.
 */
function readBucketMap(value: unknown): ProductRankingBucketMap {
  const map: ProductRankingBucketMap = {};
  if (value == null || typeof value !== 'object') return map;
  for (const [key, count] of Object.entries(value)) {
    if (typeof count === 'number' && Number.isFinite(count)) map[key] = count;
  }
  return map;
}

/** 목록·보드의 패싯 — 날짜가 고정돼 있어 **문서 수**다 */
function readFacets(value: unknown): ProductRankingFacets {
  const facets = readObject(value);
  return {
    sources: readBucketMap('sources' in facets ? facets.sources : null),
    kinds: readBucketMap('kinds' in facets ? facets.kinds : null),
    brands: readBucketMap('brands' in facets ? facets.brands : null),
  };
}

/**
 * 검색의 패싯 — 날짜 무관이라 **고유 상품 수**다.
 *
 * ⚠️ 목록 패싯과 타입은 같지만 세는 단위가 다르다. 나란히 놓고 비교하면 안 된다.
 * ⚠️ `tags`는 상세 수집이 된 상품에만 붙어 분포가 전체를 대표하지 않는다.
 */
function readSearchFacets(value: unknown): ProductRankingSearchFacets {
  const facets = readObject(value);
  const tags = readObject('tags' in facets ? facets.tags : null);
  return {
    ...readFacets(facets),
    // ⚠️ 키를 서버가 정한 축이라 0이어도 남는다 — 칩이 사라지지 않는다
    prices: readBucketMap('prices' in facets ? facets.prices : null),
    withDetail: readNumber('withDetail' in facets ? facets.withDetail : null),
    tags: {
      productType: readBucketMap('productType' in tags ? tags.productType : null),
      form: readBucketMap('form' in tags ? tags.form : null),
      concern: readBucketMap('concern' in tags ? tags.concern : null),
      ingredient: readBucketMap('ingredient' in tags ? tags.ingredient : null),
      area: readBucketMap('area' in tags ? tags.area : null),
    },
  };
}

/** 화면이 건 태그 필터를 서버가 그대로 돌려준다 — 아는 다섯 개만 남긴다 */
function readTagFilter(value: unknown): ProductRankingTagFilter {
  const raw = readObject(value);
  const tags: ProductRankingTagFilter = {};
  const keys = ['productType', 'form', 'concern', 'ingredient', 'area'] as const;
  for (const key of keys) {
    const text = readText(key in raw ? (raw as Record<string, unknown>)[key] : null);
    if (text) tags[key] = text;
  }
  return tags;
}

/**
 * 이력 요약.
 *
 * ⚠️ 점이 하나도 없으면 서버가 `null`을 준다 — **`0`으로 채우지 않는다.**
 * ⚠️ 할인율 셋만 `number`이고 나머지는 `number | null`이다.
 */
function readHistorySummary(value: unknown): ProductRankingHistorySummary | null {
  if (value == null || typeof value !== 'object') return null;
  const get = (key: string) => (key in value ? (value as Record<string, unknown>)[key] : null);
  const orNull = (raw: unknown): number | null =>
    (typeof raw === 'number' && Number.isFinite(raw) ? raw : null);

  return {
    days: readNumber(get('days')),
    firstDate: readText(get('firstDate')),
    lastDate: readText(get('lastDate')),
    firstRank: orNull(get('firstRank')),
    lastRank: orNull(get('lastRank')),
    bestRank: orNull(get('bestRank')),
    worstRank: orNull(get('worstRank')),
    rankDelta: orNull(get('rankDelta')),
    firstPrice: orNull(get('firstPrice')),
    lastPrice: orNull(get('lastPrice')),
    priceDelta: orNull(get('priceDelta')),
    priceDropPct: orNull(get('priceDropPct')),
    firstDiscount: readNumber(get('firstDiscount')),
    lastDiscount: readNumber(get('lastDiscount')),
    discountDelta: readNumber(get('discountDelta')),
  };
}

/**
 * 수집일 목록 (`rankings.availableDates`) — 스냅샷이 있는 날(최신순)과 그날의 문서 수(상품 × 하루) · 채널별 수.
 * ⚠️ 문서 하나가 「상품 하나 × 하루」다 — 날의 `total` 은 그날 모은 상품 정보 수다.
 */
export async function fetchProductRankingAvailableDates(
  params: ProductRankingAvailableDatesRequest = {},
): Promise<ProductRankingAvailableDatesResponse> {
  const response = await requestRankingOp({ op: 'rankings.availableDates', params });
  const data = readOpData(response.data, 'fetchProductRankingAvailableDates');
  return {
    dates: readList<ProductRankingAvailableDatesResponse['dates'][number]>('dates' in data ? data.dates : null),
    latest: readText('latest' in data ? data.latest : null),
  };
}

/**
 * 검색 (`rankings.search`) — **날짜 무관**이다.
 *
 * ⚠️ 목록과 다른 질문이다. 같은 상품의 여러 날짜 스냅샷을 하나로 접고 직전과 비교해
 * 변동을 낸다. 그리고 시작일 하한(`minDate`)이 있다.
 */
export async function fetchProductRankingSearch(
  params: ProductRankingSearchRequest,
): Promise<ProductRankingSearchResponse> {
  const mocked = resolveMockProductRankingSearch(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.search', params });
  const data = readOpData(response.data, 'fetchProductRankingSearch');
  return {
    ...readPaged<ProductRankingSearchItem>(data),
    q: readText('q' in data ? data.q : null),
    kind: readText('kind' in data ? data.kind : null),
    source: readText('source' in data ? data.source : null),
    region: readText('region' in data ? data.region : null),
    // ⚠️ 서버가 좁히기 조건을 되돌려 준다 — 늦게 온 응답인지 화면이 가릴 수 있다
    brand: readText('brand' in data ? data.brand : null),
    priceBand: readText('priceBand' in data ? data.priceBand : null),
    detailOnly: readBool('detailOnly' in data ? data.detailOnly : null),
    sort: readText('sort' in data ? data.sort : null),
    minDate: readText('minDate' in data ? data.minDate : null),
    minDateLabel: readText('minDateLabel' in data ? data.minDateLabel : null),
    latestDate: readText('latestDate' in data ? data.latestDate : null),
    tags: readTagFilter('tags' in data ? data.tags : null),
    facets: readSearchFacets('facets' in data ? data.facets : null),
  };
}

/**
 * 관리 상품 수와 그 늘어남 (`rankings.catalogGrowth`) — 홈 「수집 하이라이트」의 상품 칸.
 */
export async function fetchProductCatalogGrowth(params: ProductCatalogGrowthRequest): Promise<ProductCatalogGrowthResponse> {
  const response = await requestRankingOp({ op: 'rankings.catalogGrowth', params });
  const data = readOpData(response.data, 'fetchProductCatalogGrowth') as Record<string, unknown>;
  const raw = data.newByDay != null && typeof data.newByDay === 'object' ? (data.newByDay as Record<string, unknown>) : {};
  const newByDay: Record<string, number> = {};
  Object.entries(raw).forEach(([day, count]) => { newByDay[day] = readNumber(count); });
  return { total: readNumber(data.total), newByDay, sources: readList<string>(data.sources), since: readText(data.since) };
}

/**
 * 보드 (`rankings.board`) — 판매채널별 컬럼.
 *
 * ⚠️ `date`와 `requestedDate`가 다를 수 있다 — 고른 날에 스냅샷이 없으면 서버가
 * 그 이전 가장 가까운 수집일로 스냅한다.
 */
export async function fetchProductRankingBoard(
  params: ProductRankingBoardRequest,
): Promise<ProductRankingBoardResponse> {
  const mocked = resolveMockProductRankingBoard(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.board', params });
  const data = readOpData(response.data, 'fetchProductRankingBoard');
  return {
    date: readText('date' in data ? data.date : null),
    dateLabel: readText('dateLabel' in data ? data.dateLabel : null),
    requestedDate: readText('requestedDate' in data ? data.requestedDate : null),
    previousDate: readText('previousDate' in data ? data.previousDate : null),
    previousDateLabel: readText('previousDateLabel' in data ? data.previousDateLabel : null),
    hasPreviousSnapshot: readBool('hasPreviousSnapshot' in data ? data.hasPreviousSnapshot : null),
    availableDates: readList('availableDates' in data ? data.availableDates : null),
    isLatestDate: readBool('isLatestDate' in data ? data.isLatestDate : null),
    kind: readText('kind' in data ? data.kind : null),
    columns: readList('columns' in data ? data.columns : null),
    total: readNumber('total' in data ? data.total : null),
    facets: readFacets('facets' in data ? data.facets : null),
  };
}

/**
 * 상세 (`rankings.detail`).
 *
 * ⚠️ 응답이 **두 갈래 유니온**이다 — `found`로 갈린다. 못 찾은 쪽은 필드가 다섯뿐이라
 * 여기서 `found`를 보고 갈라 준다. 그래야 화면이 `if (!data.found) return`으로 끝난다.
 */
export async function fetchProductRankingDetail(
  params: ProductRankingDetailRequest,
): Promise<ProductRankingDetailResponse> {
  const mocked = resolveMockProductRankingDetail(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.detail', params });
  const data = readOpData(response.data, 'fetchProductRankingDetail');

  if (!('found' in data) || data.found !== true) {
    return {
      postId: readText('postId' in data ? data.postId : null),
      source: readText('source' in data ? data.source : null),
      kind: readText('kind' in data ? data.kind : null),
      found: false,
      hasDetail: false,
    };
  }
  if (!isDetailFound(data)) {
    throw new Error('[fetchProductRankingDetail] found=true인데 상세 모양이 아니다');
  }
  return data;
}

/**
 * 닻이 되는 필드 셋만 본다 — 30여 필드를 다 세지 않는다(파일 머리말 ⚠️).
 * 서버가 이 셋을 채웠다면 나머지도 상세 조립기가 채운 것이다.
 */
function isDetailFound(
  value: object,
): value is Extract<ProductRankingDetailResponse, { found: true }> {
  return 'title' in value && typeof value.title === 'string'
    && 'review' in value && value.review != null && typeof value.review === 'object'
    && 'tags' in value && value.tags != null && typeof value.tags === 'object'
    && 'media' in value && Array.isArray(value.media);
}

/**
 * 상세페이지 **한 버전** (`rankings.detailVersion`).
 *
 * ⚠️ 응답이 **두 갈래 유니온**이다 — `found`로 갈린다. 못 찾은 쪽(지문 도입 이전 수집분)은
 * 필드가 넷뿐이라 여기서 갈라 준다. 그래야 화면이 `if (!data.found)`로 끝난다.
 *
 * ⚠️ `media`를 다시 세지 않는다 — 컷 하나가 6필드고 화면이 빈 배열을 견딘다(파일 머리말).
 */
export async function fetchProductDetailVersion(
  params: ProductDetailVersionRequest,
): Promise<ProductDetailVersionResponse> {
  const response = await requestRankingOp({ op: 'rankings.detailVersion', params });
  const data = readOpData(response.data, 'fetchProductDetailVersion');

  if (!('found' in data) || data.found !== true) {
    return {
      postId: readText('postId' in data ? data.postId : null),
      source: readText('source' in data ? data.source : null),
      seq: readNumber('seq' in data ? data.seq : null),
      found: false,
    };
  }

  return {
    postId: readText('postId' in data ? data.postId : null),
    source: readText('source' in data ? data.source : null),
    seq: readNumber('seq' in data ? data.seq : null),
    found: true,
    hash: readText('hash' in data ? data.hash : null),
    firstDate: readText('firstDate' in data ? data.firstDate : null),
    lastDate: readText('lastDate' in data ? data.lastDate : null),
    // ⚠️ `0`과 「모른다」가 다른 값이라 `readNumber`(없으면 0)로 접지 않는다
    assetCount: readNumberOrNull('assetCount' in data ? data.assetCount : null),
    media: readList('media' in data ? data.media : null),
    textAll: readText('textAll' in data ? data.textAll : null),
  };
}

/** 이력 (`rankings.history`) — 날짜 오름차순. 점이 없으면 `summary`가 `null` */
export async function fetchProductRankingHistory(
  params: ProductRankingHistoryRequest,
): Promise<ProductRankingHistoryResponse> {
  const mocked = resolveMockProductRankingHistory(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.history', params });
  const data = readOpData(response.data, 'fetchProductRankingHistory');
  return {
    postId: readText('postId' in data ? data.postId : null),
    source: readText('source' in data ? data.source : null),
    kind: readText('kind' in data ? data.kind : null),
    points: readList('points' in data ? data.points : null),
    summary: readHistorySummary('summary' in data ? data.summary : null),
  };
}

/**
 * 타 채널 같은 상품 (`rankings.crossChannel`).
 *
 * ## ⚠️ 응답이 **두 갈래 유니온**이다 — `matchedBy`로 갈린다
 *
 * 브랜드를 모르면 서버가 매칭을 **아예 건너뛴다**(오탐이 너무 많아서). 그때는 200에
 * `{ items: [], matchedBy: 'none', note }`이고 **`note`에 건너뛴 이유가 담긴다.**
 * 화면이 그 문구를 그대로 보여 준다.
 *
 * ⚠️ 여기서 `matchedBy: 'brand+title'`을 고정으로 적으면 **타입은 통과하면서 `note`가
 * 조용히 사라진다** — 브랜드가 있는 표본만 보면 건너뛴 갈래를 한 번도 안 타서 눈에 띄지 않는다.
 */
export async function fetchProductCrossChannel(
  params: ProductRankingCrossChannelRequest,
): Promise<ProductRankingCrossChannelResponse> {
  const mocked = resolveMockProductCrossChannel(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.crossChannel', params });
  const data = readOpData(response.data, 'fetchProductCrossChannel');

  if (('matchedBy' in data ? data.matchedBy : null) !== 'brand+title') {
    return {
      items: [],
      matchedBy: 'none',
      note: readText('note' in data ? data.note : null),
    };
  }

  return {
    postId: readText('postId' in data ? data.postId : null),
    source: readText('source' in data ? data.source : null),
    brand: readText('brand' in data ? data.brand : null),
    normalizedTitle: readText('normalizedTitle' in data ? data.normalizedTitle : null),
    candidates: readNumber('candidates' in data ? data.candidates : null),
    items: readList('items' in data ? data.items : null),
    matchedBy: 'brand+title',
  };
}

/**
 * **담아 둔 상품의 얼굴** (`rankings.thumbs`) — 상품 id 여럿 → 대표 사진 한 장씩.
 *
 * 🔑 상세(`rankings.detail`)를 id 수만큼 부르지 않으려고 둔 질문이다.
 * ⚠️ 몰이 함께 온다 — 같은 상품 id 가 몰마다 따로 있어 부르는 쪽이 짝을 맞춘다.
 */
export async function fetchProductRankingThumbs(
  params: ProductRankingThumbsRequest,
): Promise<ProductRankingThumbsResponse> {
  const mocked = resolveMockProductRankingThumbs(params);
  if (mocked) return mocked;

  const response = await requestRankingOp({ op: 'rankings.thumbs', params });
  const data = readOpData(response.data, 'fetchProductRankingThumbs');
  const raw = 'thumbs' in data && Array.isArray(data.thumbs) ? data.thumbs : [];
  const thumbs: ProductRankingThumb[] = [];
  for (const item of raw) {
    const row = (item != null && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const postId = readText(row.postId);
    const image = readText(row.image);
    if (postId && image) thumbs.push({ postId, source: readText(row.source).toLowerCase(), image });
  }
  return { thumbs };
}

/**
 * **담아 둔 상품 여럿의 카드** (`rankings.byRefs`) — 상세·이력 단건 둘의 같은 칸을 상품마다 한 줄로.
 *
 * ⚠️ 답은 묻은 좌표와 **같은 순서·같은 길이**다 · 한 번에 `PRODUCT_BY_REFS_MAX` 개까지(부르는 쪽이 나눈다).
 * ⚠️ 모양이 아닌 줄은 「못 읽었다」(`ok: false`)로 읽는다.
 * mockup 이 상세·이력 단건 답을 둘 다 쥔 상품은 그 답으로 짓고, 나머지만 묻는다.
 */
export async function fetchProductRankingsByRefs(
  params: ProductRankingByRefsRequest,
): Promise<ProductRankingByRefsResponse> {
  const items: (ProductRankingRefCard | null)[] = params.refs.map((ref) => {
    const detail = resolveMockProductRankingDetail(ref);
    const history = resolveMockProductRankingHistory(ref);
    return detail && history ? toRefCard(detail, history) : null;
  });
  const asked = params.refs.filter((_, i) => items[i] == null);
  if (asked.length === 0) return { items: items as ProductRankingRefCard[] };

  const response = await requestRankingOp({ op: 'rankings.byRefs', params: { refs: asked } });
  const data = readOpData(response.data, 'fetchProductRankingsByRefs');
  const raw = 'items' in data && Array.isArray(data.items) ? data.items : [];
  let next = 0;
  return { items: items.map((item) => item ?? readRefCard(raw[next++])) };
}

/** 상세·이력 단건 답 → 카드 한 줄(서버 `rankings-by-refs` 와 같은 칸) */
function toRefCard(
  detail: ProductRankingDetailResponse,
  history: ProductRankingHistoryResponse,
): ProductRankingRefCard {
  if (detail.found !== true) return { ok: true, found: false };
  const { points } = history;
  return {
    ok: true,
    found: true,
    title: detail.title,
    brand: detail.brand,
    link: detail.link,
    image: detail.images[0] ?? '',
    reviewAvg: detail.review.avg,
    reviewCount: detail.review.count,
    date: detail.date,
    firstPoint: points[0] ?? null,
    lastPoint: points[points.length - 1] ?? null,
  };
}

function readRefCard(value: unknown): ProductRankingRefCard {
  if (value == null || typeof value !== 'object' || !('ok' in value) || value.ok !== true) return { ok: false };
  if (!('found' in value) || value.found !== true) return { ok: true, found: false };
  const row = value as Record<string, unknown>;
  return {
    ok: true,
    found: true,
    title: readText(row.title),
    brand: readText(row.brand),
    link: readText(row.link),
    image: readText(row.image),
    reviewAvg: readNumberOrNull(row.reviewAvg),
    reviewCount: readNumberOrNull(row.reviewCount),
    date: readText(row.date),
    firstPoint: readPoint(row.firstPoint),
    lastPoint: readPoint(row.lastPoint),
  };
}

/** 이력 한 점 — 단건 이력(`fetchProductRankingHistory`)처럼 서버 모양을 그대로 통과시킨다 */
function readPoint(value: unknown): ProductRankingHistoryPoint | null {
  return value != null && typeof value === 'object' ? (value as ProductRankingHistoryPoint) : null;
}
